import React, { useState, useEffect, FC } from 'react';
import styled from 'styled-components';

import CheckmarkSVG from '../../assets/icons/bullet-check-icon.svg';
import { colors } from '../constants';

// LoadingRing
// Animates a fill bar within a circular track to simulate loading completion
// Upon fill completion, executes completion callback passed in props

// Props:
//   completionCallback: execute when final animation has completed
//   allowCompletion: immediately trigger final fill animation to conclude simulation
//   radius: length in pixels from center to edge of ring
//   stroke: width of orange fill within the track

// MiniLoadingRing
// Used in VerificationWaitingRoom
// A progress step indicator corresponding to an arc of the main LoadingRing

interface LoadingRingProps {
  id: string;
  completionCallback: Function;
  allowCompletion: boolean;
  radius?: number;
  stroke?: number;
}

interface MiniLoadingProps {
  id: string;
  completionCallback: Function;
  start: boolean;
  completed: boolean;
  allowCompletion: boolean;
}

// Props for styled component 'Fill'
interface StyledProps {
  completed?: boolean;
  allowCompletion?: boolean;
}

// LoadingRing animation constants
const FIRST_ANIMATION_DELAY = 500;
const ANIMATION_DURATION = 4500;
const FINAL_ANIMATION_DURATION = 500;
const MAX_FILL_BEFORE_COMPLETE = 0.96;

// MiniLoadingRing animation constants
const MINI_ANIMATION_DURATION = 30000;
const MINI_FINAL_ANIMATION_DURATION = 500;

// #region -- Styles ------------------------------------------------------------------------------
// Main loading circle container
const Container = styled.div`
  position: relative;
  height: 6.875rem;
  width: 6.875rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;
Container.displayName = 'Container';

// Svg contains the circles for track, hole, and fill
const Svg = styled.svg`
  height: 6.875rem;
  width: 6.875rem;
`;
Svg.displayName = 'Svg';

// Number in center of main loading circle
const Percentage = styled.p`
  position: absolute;
  padding: 0 0 0 0.3125rem;
  font-weight: 500;
  color: ${colors.TEXT_PRIMARY};
`;
Percentage.displayName = 'Percentage';

// Light gray circle centered inside svg container
const Track = styled.circle`
  fill: #f4f4f4;
`;
Track.displayName = 'Track';

// White inner circle centered inside gray circle
const Hole = styled.circle`
  fill: #fff;
`;
Hole.displayName = 'Hole';

// Orange progress fill
const Fill = styled.circle<StyledProps>`
  fill: transparent;
  stroke: ${colors.UI_PRIMARY_ACCENT};
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  transition: ${ANIMATION_DURATION}ms stroke-dashoffset ease-out;
  ${(p: StyledProps) =>
    p.allowCompletion &&
    `
    transition: ${FINAL_ANIMATION_DURATION}ms stroke-dashoffset ease-out;
  `};
  ${(p: StyledProps) =>
    p.completed &&
    `
    stroke: ${p.completed ? '#58AB6A' : colors.UI_PRIMARY_ACCENT};
  `};
`;
Fill.displayName = 'Fill';

const MiniComplete = styled.div`
  height: 1rem;
  width: 1rem;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${colors.UI_PRIMARY_ACCENT};
  svg {
    height: 100%;
    width: 100%;
  }
`;
MiniComplete.displayName = 'MiniComplete';

// Mini loading circle container
const MiniContainer = styled(Container)`
  height: 1rem;
  width: 1rem;
`;
MiniContainer.displayName = 'MiniContainer';

// Mini loading circle svg contains MiniTrack, Hole, and MiniFill
const MiniSvg = styled.svg`
  height: 1rem;
  width: 1rem;
`;
MiniSvg.displayName = 'MiniSvg';

// Mini Track with a slightly darker fill
const MiniTrack = styled(Track)`
  fill: #e8e8e8;
`;
MiniTrack.displayName = 'MiniTrack';

// Mini Fill with a continuous animation duration rather than periodic
const MiniFill = styled(Fill)`
  transition: ${MINI_ANIMATION_DURATION}ms stroke-dashoffset linear;
  ${(p: StyledProps) =>
    p.allowCompletion &&
    `
    transition: ${FINAL_ANIMATION_DURATION}ms stroke-dashoffset ease-out;
  `};
`;
MiniFill.displayName = 'MiniFill';
// #endregion

export const MiniLoadingRing: FC<MiniLoadingProps> = ({
  id,
  completionCallback,
  start,
  completed,
  allowCompletion,
}) => {
  // Calculate svg circle attributes for track, hole, and fill
  const radius = 8;
  const stroke = 3;
  const holeRadius = radius - stroke;
  const fillRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * fillRadius;
  const strokeDasharray = `${circumference} ${circumference}`;

  const [offset, setOffset] = useState(circumference);

  // Begin filling the track continuously when allowed via props.start
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (start) {
      setOffset(0);
      timer = setTimeout(() => {
        completionCallback();
      }, MINI_ANIMATION_DURATION);
    }
    return () => clearTimeout(timer);
  }, [start]);

  // Fill up the ring right away when loading simulation should end
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (allowCompletion && !completed) {
      setOffset(0.0000001);
      timer = setTimeout(() => {
        completionCallback();
      }, MINI_FINAL_ANIMATION_DURATION);
    }
    return () => clearTimeout(timer);
  }, [allowCompletion]);

  if (completed) {
    return (
      <MiniComplete id={`${id}-complete-container`}>
        <CheckmarkSVG id={`${id}-complete-checkmark-svg`} strokeWidth={1.5} />
      </MiniComplete>
    );
  }

  return (
    <MiniContainer id={id}>
      <MiniSvg id={`${id}-svg`}>
        <MiniTrack id={`${id}-track`} r={radius} cx={radius} cy={radius} />
        <Hole id={`${id}-hole`} r={holeRadius} cx={radius} cy={radius} />
        <MiniFill
          id={`${id}-fill`}
          r={fillRadius}
          cx={radius}
          cy={radius}
          strokeWidth={stroke}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={offset}
          strokeLinecap="round"
          allowCompletion={allowCompletion}
        />
      </MiniSvg>
    </MiniContainer>
  );
};

// Main LoadingRing component
export const LoadingRing: FC<LoadingRingProps> = ({
  id,
  completionCallback,
  allowCompletion,
  radius,
  stroke,
}) => {
  // Calculate svg circle attributes for track, hole, and fill
  const holeRadius = radius - stroke;
  const fillRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * fillRadius;
  const strokeDasharray = `${circumference} ${circumference}`;

  const [offset, setOffset] = useState(circumference);
  const [fill, setFill] = useState(1 - offset / circumference);

  // Increment a small arc after a brief delay when page first loads
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!allowCompletion) {
      timer = setTimeout(() => {
        const incrementedFill = getIncrementedFill();
        setFill(incrementedFill);
      }, FIRST_ANIMATION_DELAY);
    }
    return () => clearTimeout(timer);
  }, []);

  // Simulate progress by periodically incrementing the arc length of Fill
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!allowCompletion) {
      timer = setTimeout(() => {
        const incrementedFill = getIncrementedFill();
        setFill(incrementedFill);
      }, ANIMATION_DURATION);
    }
    return () => clearTimeout(timer);
  }, [fill]);

  // Calculate and set the Fill stroke offset using the newly incremented fill
  useEffect(() => {
    const remainingFill = 1 - fill;
    const remainingOffset = circumference * remainingFill;
    setOffset(remainingOffset);
  }, [fill]);

  // Fill the ring all the way up when completion is allowed
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (allowCompletion) {
      setFill(1);
      // Execute completion logic upstream when final animation is done
      timer = setTimeout(() => {
        completionCallback();
      }, FINAL_ANIMATION_DURATION);
    }
    return () => clearTimeout(timer);
  }, [allowCompletion]);

  // Get value for next fill, up to 0.1 greater than current value of fill
  function getIncrementedFill() {
    // Increment at most by 10% of total ring
    const increment = Math.random() * 0.1;
    // Do not exceed the max allowed fill
    const incrementedFill = Math.min(
      fill + increment,
      MAX_FILL_BEFORE_COMPLETE
    );
    return incrementedFill;
  }

  return (
    <Container id={id}>
      <Svg id={`${id}-svg`}>
        <Track id={`${id}-track`} r={radius} cx={radius} cy={radius} />
        <Hole id={`${id}-hole`} r={holeRadius} cx={radius} cy={radius} />
        <Fill
          id={`${id}-fill`}
          r={fillRadius}
          cx={radius}
          cy={radius}
          strokeWidth={stroke}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={offset}
          strokeLinecap="round"
          completed={fill === 1}
          allowCompletion={allowCompletion}
        />
      </Svg>
      <Percentage id={`${id}-percentage`}>
        {`${Math.round(100 * fill)}%`}
      </Percentage>
    </Container>
  );
};

LoadingRing.defaultProps = {
  radius: 55,
  stroke: 6,
};
